<template>
    <div
        class="p-6 bg-slate-300/30 rounded-2xl shadow-inner border-solid border-b border-t-0 border-x-0 border-white flex-col gap-4 flex"
    >
        <kiosk-ten-dlc-checkbox-filter
            label="Source"
            name="source"
            :options="sourceOptions"
            v-model="source"
        ></kiosk-ten-dlc-checkbox-filter>
        <div>
            <div class="font-bold text-xs text-slate-700 mb-2">Brand</div>
            <vx-selectable
                v-model="filters.brand_id"
                placeholder="-"
                :reduce="(option: { label: string; value: string }) => option.value"
                :loading="loadingBrands"
                :options="brandOptions"
                excludeSelected
                @search="searchBrands"
                :clearable="false"
                data-test="brands-selectable"
            >
            </vx-selectable>
        </div>

        <kiosk-ten-dlc-checkbox-filter
            label="Status"
            name="status"
            :options="statusOptions"
            v-model="statuses"
        ></kiosk-ten-dlc-checkbox-filter>

        <kiosk-ten-dlc-checkbox-filter
            label="DCA Complete"
            name="dca-complete"
            :options="dcaCompleteOptions"
            v-model="dcaComplete"
        ></kiosk-ten-dlc-checkbox-filter>

        <kiosk-ten-dlc-checkbox-filter
            label="AT&T Class"
            name="bucket"
            :options="attClassBucketOptions"
            v-model="attClassBuckets"
        ></kiosk-ten-dlc-checkbox-filter>

        <kiosk-ten-dlc-checkbox-filter
            label="T-Mobile Tier"
            name="tmobile-tier"
            :options="tmobileTierStateOptions"
            v-model="tmobileTierStates"
        ></kiosk-ten-dlc-checkbox-filter>

        <kiosk-ten-dlc-checkbox-filter
            label="Terms"
            name="terms"
            :options="termsStateOptions"
            v-model="termsStates"
        ></kiosk-ten-dlc-checkbox-filter>

        <kiosk-ten-dlc-checkbox-filter
            label="Numbers"
            name="has-phone-numbers"
            :options="phoneNumbersOptions"
            v-model="hasPhoneNumbers"
        ></kiosk-ten-dlc-checkbox-filter>
    </div>
</template>

<script lang="ts" setup>
import { VxSelectable } from '@voxie/frontend-components';
import { useTcrBrandOptions } from '~/components/kiosk/kiosk-ten-dlc/composables/useTcrBrandOptions';
import { listSelection, scalarSelection } from '~/components/kiosk/kiosk-ten-dlc/filterSelection';
import KioskTenDlcCheckboxFilter from '~/components/kiosk/kiosk-ten-dlc/list/KioskTenDlcCheckboxFilter.vue';
import {
    attClassBucketOptions,
    campaignStatusLabels,
    sourceLabels,
    termsStateOptions,
    tmobileTierStateOptions,
} from '~/components/kiosk/kiosk-ten-dlc/tenDlcLabels';
import type { TcrCampaignStatus, TcrListParams } from '~/services/adminTenDlcClient.types';

const sourceOptions: { label: string; value: NonNullable<TcrListParams['source']> }[] = [
    { label: sourceLabels.tcr, value: 'tcr' },
    { label: sourceLabels.twilio, value: 'twilio' },
];

const statusOptions: { label: string; value: TcrCampaignStatus }[] = [
    { label: campaignStatusLabels.active, value: 'active' },
    { label: campaignStatusLabels.pending, value: 'pending' },
    { label: campaignStatusLabels.expired, value: 'expired' },
    { label: campaignStatusLabels.failed, value: 'failed' },
];

// 1/0 because qs serialises false as the string "false", which Laravel's boolean rule rejects.
const dcaCompleteOptions: { label: string; value: NonNullable<TcrListParams['dca_complete']> }[] = [
    { label: 'Yes', value: 1 },
    { label: 'No', value: 0 },
];

const phoneNumbersOptions: { label: string; value: NonNullable<TcrListParams['has_phone_numbers']> }[] = [
    { label: 'Has numbers', value: 1 },
    { label: 'No numbers', value: 0 },
];

const filters = defineModel<TcrListParams>({ default: () => ({}) });

const source = scalarSelection(
    () => filters.value.source,
    (value) => {
        filters.value.source = value;
    }
);

const statuses = listSelection(
    () => filters.value.statuses,
    (values) => {
        filters.value.statuses = values;
    }
);

const dcaComplete = scalarSelection(
    () => filters.value.dca_complete,
    (value) => {
        filters.value.dca_complete = value;
    }
);

const attClassBuckets = listSelection(
    () => filters.value.att_class_buckets,
    (values) => {
        filters.value.att_class_buckets = values;
    }
);

const tmobileTierStates = listSelection(
    () => filters.value.tmobile_tier_states,
    (values) => {
        filters.value.tmobile_tier_states = values;
    }
);

const termsStates = listSelection(
    () => filters.value.terms_states,
    (values) => {
        filters.value.terms_states = values;
    }
);

const hasPhoneNumbers = scalarSelection(
    () => filters.value.has_phone_numbers,
    (value) => {
        filters.value.has_phone_numbers = value;
    }
);

const { brandOptions, loadingBrands, searchBrands } = useTcrBrandOptions(() => filters.value.brand_id);

searchBrands();
</script>
